import { VALTYPES } from '../../../../data/interfaces';
import { categoricalOrderOptions } from '../../helpers/categoricalSorters';
import {
  createConfigParamBoolean,
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeDiscreteColorScheme,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { CONFIGPARAM_VISIBILITYCONDITION_TYPES, TpChartManifest } from '../interfaces';
import { Violinplot } from './Violinplot';

export const manifestViolinplot: TpChartManifest = {
  id: 'ViolinPlot',
  name: 'Violin plot',
  description: `Vizualises the **relationship** between a **categorical** and **numerical** property.
Each state of the categorical property defines another column, and the values of the numerical propertery are shown density distributions ("violins") on a vertical axis in that column.

Optionally, these distributions can also be shown as boxs and whiskers plots, indication the median, 25 and 75 percentiles and minimum and maximum values.`,
  component: Violinplot,
  canvasBased: true,
  groups: [{ groupId: 'categories', groupName: 'Categories' }],
  aspects: [
    {
      id: 'categories',
      name: 'Categories data',
      groupId: 'categories',
      description: 'States of this property define the columns in the plot.',
      required: true,
      quality: 1,
      dataType: VALTYPES.CATEGORICAL,
    },
    {
      id: 'values',
      name: 'Values data',
      groupId: 'categories',
      description: 'Used to show distributions within each column.',
      required: true,
      quality: 1,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
    },
  ],
  configParams: [
    {
      id: 'sortBy',
      name: 'Sort by',
      groupId: 'categories',
      description: 'How to sort the columns (states of the `categories` property).',
      paramType: createConfigParamMultipleChoice([
        ...categoricalOrderOptions,
        { id: 'avgValInc', name: 'Increasing average value' },
        { id: 'avgValDec', name: 'Decreasing average value' },
      ]),
    },
    {
      id: 'labelSpace',
      name: 'Label space',
      groupId: 'categories',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different states.',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'kernelSizeFactor',
      name: 'Kernel size',
      groupId: 'categories',
      description: 'Scaling factor that determines the snoothness of the generated density distribution',
      paramType: createConfigParamTypeRange(0.1, 1, 0.05, 0.2),
    },
    {
      id: 'densityGammaFactor',
      name: 'Gamma factor',
      groupId: 'categories',
      description: `Scaling factor that defines a nonlinear relation between the computed density value and the displayed value in the plot. 
This can be used to emphasise areas with low densities.`,
      paramType: createConfigParamTypeRange(5, 10, 0.5, 10),
    },
    {
      id: 'displayType',
      name: 'Display',
      groupId: 'categories',
      description: 'Type of visualization used.',
      paramType: createConfigParamMultipleChoice([
        { id: 'densityBox', name: 'Density + box' },
        { id: 'density', name: 'Density only' },
        { id: 'boxMinMaxWhiskers', name: 'Box & whiskers (min-max)' },
      ]),
    },
    {
      id: 'displayMean',
      name: 'Display mean',
      groupId: 'categories',
      description: 'Whether to indicate the mean value in the plot.',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'colorType',
      name: 'Color',
      groupId: 'categories',
      description: 'Indicates how the density distributions should be colored.',
      paramType: createConfigParamMultipleChoice([
        { id: 'none', name: 'None' },
        { id: 'categories', name: 'From categories' },
      ]),
    },

    {
      id: `categoriesSchemeCategorical`,
      name: `Color scheme`,
      groupId: 'categories',
      paramType: createConfigParamTypeDiscreteColorScheme(),
      visibilityConditions: [
        {
          conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.CONFIG_VALUE,
          conditionInfo: {
            configParam: 'colorType',
            configValue: 'categories',
          },
        },
      ],
    },
  ],
  examples: [
    {
      id: 'violinplot_1',
    },
    {
      id: 'violinplot_2',
    },
  ],
};
